import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../constants";

const STATUS_COLOR = {
  ok: "#A3E635",
  degraded: "#FBBF24",
  down: "#F87171",
};

const Dot = ({ status }) => (
  <span
    className="inline-block w-2.5 h-2.5 rounded-full"
    style={{ background: STATUS_COLOR[status] || "#6B7280", boxShadow: `0 0 8px ${STATUS_COLOR[status] || "#6B7280"}` }}
  />
);

/**
 * System Status dashboard card.
 * Shows uptime, app version, ZIP build hashes, DB stats, and live integration health.
 * Auto-refreshes every 30s while mounted.
 */
export default function SystemStatusCard({ adminPassword }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const res = await axios.post(`${API}/admin/system-status`, { password: adminPassword });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to fetch status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    if (!adminPassword) return;
    fetchStatus(false);
    const id = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(id);
  }, [adminPassword, fetchStatus]);

  if (loading) {
    return <div className="card-glass p-6 text-[#E8E4DC]" data-testid="system-status-loading">Loading system status…</div>;
  }
  if (error || !data) {
    return (
      <div className="card-glass p-6 border border-red-500/30" data-testid="system-status-error">
        <p className="text-red-400 font-semibold">System status unavailable</p>
        <p className="text-[#E8E4DC]/70 text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="card-glass p-6 mb-8" data-testid="system-status-card">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Dot status={data.overall} />
          <div>
            <h2 className="font-heading text-xl font-semibold text-white">System Status</h2>
            <p className="text-[#E8E4DC]/60 text-xs">Top Screen™ v{data.app_version} · {data.backend.uptime_human} uptime</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#E8E4DC]/60 text-xs" data-testid="system-status-checked">
            Updated {new Date(data.checked_at).toLocaleTimeString()}
          </span>
          <button
            onClick={() => fetchStatus(true)}
            disabled={refreshing}
            className="text-xs px-3 py-1 rounded-full border border-white/15 text-[#A3E635] hover:bg-[#A3E635]/10 disabled:opacity-50 transition-colors"
            data-testid="system-status-refresh"
          >
            {refreshing ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Two columns: Integrations + Database */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        {/* Integrations */}
        <div data-testid="system-status-integrations">
          <h3 className="text-[#E8E4DC]/80 text-xs uppercase tracking-wider font-semibold mb-2">Integrations</h3>
          <ul className="space-y-1.5">
            {data.integrations.map((i) => (
              <li key={i.name} className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-white/3">
                <span className="flex items-center gap-2">
                  <Dot status={i.status} />
                  <span className="text-white capitalize">{i.name.replace("_", " · ")}</span>
                </span>
                <span className="text-[#E8E4DC]/50 text-xs font-mono">
                  {i.status === "down" ? "unreachable" : `${i.http} · ${i.latency_ms}ms`}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Database + Config */}
        <div data-testid="system-status-database">
          <h3 className="text-[#E8E4DC]/80 text-xs uppercase tracking-wider font-semibold mb-2">Database</h3>
          <div className="flex items-center gap-2 mb-2 text-sm">
            <Dot status={data.database.status} />
            <span className="text-white">MongoDB</span>
            <span className="text-[#E8E4DC]/50 text-xs font-mono ml-auto">{data.database.latency_ms ?? "—"}ms</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="bg-white/5 rounded p-2">
              <div className="text-[#A3E635] font-bold text-lg" data-testid="db-users-count">{data.database.users_count ?? 0}</div>
              <div className="text-[#E8E4DC]/60 text-[10px] uppercase">Users</div>
            </div>
            <div className="bg-white/5 rounded p-2">
              <div className="text-[#A3E635] font-bold text-lg" data-testid="db-lifetime-count">{data.database.lifetime_count ?? 0}</div>
              <div className="text-[#E8E4DC]/60 text-[10px] uppercase">Lifetime</div>
            </div>
            <div className="bg-white/5 rounded p-2">
              <div className="text-[#A3E635] font-bold text-lg" data-testid="db-payments-count">{data.database.payments_count ?? 0}</div>
              <div className="text-[#E8E4DC]/60 text-[10px] uppercase">Payments</div>
            </div>
          </div>
          <h3 className="text-[#E8E4DC]/80 text-xs uppercase tracking-wider font-semibold mb-1.5">Config Loaded</h3>
          <div className="flex flex-wrap gap-1.5" data-testid="system-status-config">
            {Object.entries(data.config_present).map(([k, v]) => (
              <span
                key={k}
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${v ? "border-[#A3E635]/30 text-[#A3E635] bg-[#A3E635]/5" : "border-red-500/30 text-red-400 bg-red-500/5"}`}
                title={v ? "Configured" : "Missing"}
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ZIP Build Hashes */}
      <div data-testid="system-status-downloads">
        <h3 className="text-[#E8E4DC]/80 text-xs uppercase tracking-wider font-semibold mb-2">ZIP Build Artifacts</h3>
        <div className="bg-black/30 rounded-lg overflow-hidden border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-white/5">
              <tr className="text-[#E8E4DC]/60">
                <th className="text-left p-2 font-medium">Package</th>
                <th className="text-right p-2 font-medium">Size</th>
                <th className="text-left p-2 font-medium">SHA-256</th>
                <th className="text-right p-2 font-medium hidden sm:table-cell">Built</th>
              </tr>
            </thead>
            <tbody>
              {data.downloads.map((z) => (
                <tr key={z.name} className="border-t border-white/5">
                  <td className="p-2 text-white truncate max-w-[200px]">{z.name}</td>
                  <td className="p-2 text-right text-[#E8E4DC]/80 font-mono">{z.size_mb} MB</td>
                  <td className="p-2 font-mono text-[#A3E635]">{z.sha256_12}</td>
                  <td className="p-2 text-right text-[#E8E4DC]/50 hidden sm:table-cell">
                    {new Date(z.modified_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
